import type { TFunction } from 'i18next'
import type { CasePriority, CaseStatus } from '../../../types/case-management'

export interface PriorityStyle {
  dot: string
  bgLight: string
  textLight: string
  bgDark: string
  textDark: string
}

export const PRIORITY_STYLES: Record<CasePriority, PriorityStyle> = {
  critical: {
    dot: 'bg-red-500',
    bgLight: 'rgba(239, 68, 68, 0.12)',
    textLight: 'rgb(185, 28, 28)',
    bgDark: 'rgba(239, 68, 68, 0.15)',
    textDark: 'rgb(248, 113, 113)',
  },
  high: {
    dot: 'bg-orange-500',
    bgLight: 'rgba(249, 115, 22, 0.12)',
    textLight: 'rgb(194, 65, 12)',
    bgDark: 'rgba(249, 115, 22, 0.15)',
    textDark: 'rgb(251, 146, 60)',
  },
  medium: {
    dot: 'bg-amber-500',
    bgLight: 'rgba(245, 158, 11, 0.12)',
    textLight: 'rgb(180, 83, 9)',
    bgDark: 'rgba(245, 158, 11, 0.15)',
    textDark: 'rgb(251, 191, 36)',
  },
  low: {
    dot: 'bg-slate-400',
    bgLight: 'var(--highlight)',
    textLight: 'var(--text-dim)',
    bgDark: 'var(--highlight)',
    textDark: 'var(--text-dim)',
  },
}

export const STATUS_ICONS: Record<CaseStatus, string> = {
  collection: '📥',
  analysis: '🔍',
  review: '👁️',
  reporting: '📝',
  closed: '✓',
  archived: '📦',
}

const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const MINUTES_PER_HOUR = 60
const HOURS_PER_DAY = 24
const DAYS_PER_WEEK = 7
const MS_PER_HOUR = MS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR

export function formatRelativeTime(dateString: string, translate: TFunction<'cases'>): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / MS_PER_HOUR)
  const diffDays = Math.floor(diffHours / HOURS_PER_DAY)

  if (diffHours < 1) return translate('time.justNow')
  if (diffHours < HOURS_PER_DAY) return translate('time.hoursAgo', { count: diffHours })
  if (diffDays === 1) return translate('time.yesterday')
  if (diffDays < DAYS_PER_WEEK) return translate('time.daysAgo', { count: diffDays })
  return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })
}

export function isDarkTheme(): boolean {
  const el =
    typeof document === 'undefined' ? null : document.querySelector<HTMLElement>('.dashboard')
  return el?.dataset.theme === 'dark'
}
